import { useState, useMemo } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/apiClient";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  FileText, Plus, Search, Filter, Download, Send, Eye, Edit,
  ArrowLeft, CheckCircle2, XCircle, Clock, AlertTriangle
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { format, differenceInDays } from "date-fns";
import { es } from "date-fns/locale";
import type { Invoice, InvoiceStatus } from "@/types/billing";

const statusColors: Record<InvoiceStatus, string> = {
  draft: "bg-gray-100 text-gray-700 border-gray-200",
  issued: "bg-blue-100 text-blue-700 border-blue-200",
  sent: "bg-indigo-100 text-indigo-700 border-indigo-200",
  paid: "bg-green-100 text-green-700 border-green-200",
  partial: "bg-amber-100 text-amber-700 border-amber-200",
  cancelled: "bg-red-100 text-red-700 border-red-200",
  rectified: "bg-purple-100 text-purple-700 border-purple-200",
};

const statusLabels: Record<InvoiceStatus, string> = {
  draft: "Borrador",
  issued: "Emitida",
  sent: "Enviada",
  paid: "Cobrada",
  partial: "Parcial",
  cancelled: "Anulada",
  rectified: "Rectificada",
};

const statusIcons: Record<InvoiceStatus, any> = {
  draft: Edit,
  issued: FileText,
  sent: Send,
  paid: CheckCircle2,
  partial: Clock,
  cancelled: XCircle,
  rectified: AlertTriangle,
};

export default function Invoices() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");

  const statusFilter = searchParams.get("status") || "all";
  const typeFilter = searchParams.get("type") || "all";

  // Fetch invoices
  const { data: invoices = [], isLoading } = useQuery<Invoice[]>({
    queryKey: ["billing-invoices", statusFilter, typeFilter, search],
    queryFn: async () => {
      let query = api
        .from("invoices")
        .select(`
          *,
          customer:billing_customers(id, legal_name, trade_name, tax_id),
          series:invoice_series(code, name)
        `)
        .order("issue_date", { ascending: false })
        .order("invoice_number", { ascending: false });

      if (statusFilter === "overdue") {
        query = query
          .in("status", ["issued", "sent", "partial"])
          .lt("due_date", new Date().toISOString().split('T')[0]);
      } else if (statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      if (typeFilter !== "all") {
        query = query.eq("invoice_type", typeFilter);
      }

      if (search) {
        query = query.or(`invoice_number.ilike.%${search}%,customer_name.ilike.%${search}%`);
      }

      const { data, error } = await query.limit(200);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });

  // Stats
  const stats = useMemo(() => {
    const all = invoices.filter(i => i.status !== 'cancelled');
    const draft = invoices.filter(i => i.status === 'draft');
    const pending = invoices.filter(i => ['issued', 'sent', 'partial'].includes(i.status));
    const today = new Date().toISOString().split('T')[0];
    const overdue = pending.filter(i => i.due_date && i.due_date < today);

    return {
      total: all.length,
      draft: draft.length,
      pending: pending.length,
      overdue: overdue.length,
      pendingAmount: pending.reduce((sum, i) => sum + (i.total_amount - i.paid_amount), 0),
    };
  }, [invoices]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const getDaysOverdue = (dueDate: string) => {
    const days = differenceInDays(new Date(), new Date(dueDate));
    return days > 0 ? days : 0;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/billing"><ArrowLeft className="w-4 h-4" /></Link>
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Facturas</h1>
              <p className="text-muted-foreground">Listado y gestión de facturas</p>
            </div>
          </div>
          <Button asChild>
            <Link to="/billing/invoices/new">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Factura
            </Link>
          </Button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card
            className={`cursor-pointer transition-shadow hover:shadow-md ${statusFilter === 'all' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setSearchParams({})}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-sm text-muted-foreground">Total facturas</p>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer transition-shadow hover:shadow-md ${statusFilter === 'draft' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setSearchParams({ status: 'draft' })}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-gray-600">{stats.draft}</div>
              <p className="text-sm text-muted-foreground">Borradores</p>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer transition-shadow hover:shadow-md ${statusFilter === 'issued' || statusFilter === 'sent' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setSearchParams({ status: 'issued' })}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-amber-600">{stats.pending}</div>
              <p className="text-sm text-muted-foreground">Pendientes</p>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer transition-shadow hover:shadow-md ${statusFilter === 'overdue' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setSearchParams({ status: 'overdue' })}
          >
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-red-600">{stats.overdue}</div>
              <p className="text-sm text-muted-foreground">Vencidas</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por número o cliente..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select
                value={statusFilter}
                onValueChange={(v) => {
                  const params = new URLSearchParams(searchParams);
                  if (v === 'all') params.delete('status');
                  else params.set('status', v);
                  setSearchParams(params);
                }}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="draft">Borrador</SelectItem>
                  <SelectItem value="issued">Emitida</SelectItem>
                  <SelectItem value="sent">Enviada</SelectItem>
                  <SelectItem value="paid">Cobrada</SelectItem>
                  <SelectItem value="partial">Pago parcial</SelectItem>
                  <SelectItem value="overdue">Vencidas</SelectItem>
                  <SelectItem value="cancelled">Anulada</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={typeFilter}
                onValueChange={(v) => {
                  const params = new URLSearchParams(searchParams);
                  if (v === 'all') params.delete('type');
                  else params.set('type', v);
                  setSearchParams(params);
                }}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  <SelectItem value="standard">Facturas</SelectItem>
                  <SelectItem value="rectificativa">Rectificativas</SelectItem>
                  <SelectItem value="proforma">Proformas</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Invoices Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Número</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Vencimiento</TableHead>
                  <TableHead className="text-right">Importe</TableHead>
                  <TableHead className="text-right">Pendiente</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>VeriFactu</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">Cargando...</TableCell>
                  </TableRow>
                ) : invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      No hay facturas que coincidan con los filtros
                    </TableCell>
                  </TableRow>
                ) : (
                  invoices.map((invoice) => {
                    const StatusIcon = statusIcons[invoice.status];
                    const isOverdue = invoice.due_date &&
                      invoice.due_date < new Date().toISOString().split('T')[0] &&
                      ['issued', 'sent', 'partial'].includes(invoice.status);
                    const daysOverdue = invoice.due_date ? getDaysOverdue(invoice.due_date) : 0;

                    return (
                      <TableRow key={invoice.id} className={isOverdue ? 'bg-red-50' : ''}>
                        <TableCell>
                          <Link
                            to={`/billing/invoices/${invoice.id}`}
                            className="font-mono font-medium hover:underline flex items-center gap-2"
                          >
                            {invoice.invoice_type === 'rectificativa' && (
                              <Badge variant="outline" className="text-xs">R</Badge>
                            )}
                            {invoice.invoice_type === 'proforma' && (
                              <Badge variant="outline" className="text-xs">P</Badge>
                            )}
                            {invoice.invoice_number || <span className="text-muted-foreground italic">Borrador</span>}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium truncate max-w-[200px]">
                              {(invoice as any).customer?.trade_name ||
                               (invoice as any).customer?.legal_name ||
                               invoice.customer_name}
                            </p>
                            <p className="text-xs text-muted-foreground font-mono">
                              {invoice.customer_tax_id}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {format(new Date(invoice.issue_date), "dd/MM/yyyy")}
                        </TableCell>
                        <TableCell>
                          {invoice.due_date ? (
                            <div className={isOverdue ? 'text-red-600' : ''}>
                              {format(new Date(invoice.due_date), "dd/MM/yyyy")}
                              {isOverdue && (
                                <p className="text-xs">
                                  {daysOverdue} días vencida
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(invoice.total_amount)}
                        </TableCell>
                        <TableCell className="text-right">
                          {invoice.status === 'paid' ? (
                            <span className="text-green-600">0,00 €</span>
                          ) : (
                            <span className={invoice.paid_amount > 0 ? 'text-amber-600' : ''}>
                              {formatCurrency(invoice.total_amount - invoice.paid_amount)}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={statusColors[invoice.status]}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusLabels[invoice.status]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {invoice.verifactu_status === 'accepted' ? (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              OK
                            </Badge>
                          ) : invoice.verifactu_status === 'rejected' ? (
                            <Badge variant="destructive">
                              <XCircle className="w-3 h-3 mr-1" />
                              Error
                            </Badge>
                          ) : invoice.verifactu_status === 'submitted' ? (
                            <Badge variant="outline">
                              <Clock className="w-3 h-3 mr-1" />
                              Enviada
                            </Badge>
                          ) : invoice.status !== 'draft' ? (
                            <Badge variant="outline" className="text-muted-foreground">
                              Pendiente
                            </Badge>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" asChild>
                              <Link to={`/billing/invoices/${invoice.id}`}>
                                <Eye className="w-4 h-4" />
                              </Link>
                            </Button>
                            {invoice.status === 'draft' && (
                              <Button variant="ghost" size="sm" asChild>
                                <Link to={`/billing/invoices/${invoice.id}/edit`}>
                                  <Edit className="w-4 h-4" />
                                </Link>
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pending Amount Summary */}
        {stats.pendingAmount > 0 && (
          <Card>
            <CardContent className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-amber-500" />
                <span className="text-muted-foreground">Total pendiente de cobro:</span>
              </div>
              <span className="text-xl font-bold text-amber-600">
                {formatCurrency(stats.pendingAmount)}
              </span>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
